import { useState, useEffect } from "react";
import { FaArrowLeft } from "react-icons/fa6";
import SuccessModal from "./successModal/SuccessModal";
import { submitAnswers } from "../../../services/submitQuestions";
import ShowTable from "./ShowTable";
import PhoneInput from "./PhoneInput"; // Importing the PhoneInput component
import EmailInput from "./EmailInput"; // Importing the EmailInput component
import "./Questions.css";
import { base_url } from "../../../config";
import ReactPixel from 'react-facebook-pixel';

const CustomDatePicker = ({ onChange }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const daysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();

  const handleDateClick = (day) => {
    const date = new Date(currentYear, currentMonth.getMonth(), day);
    setSelectedDate(date);
    onChange(date.toISOString().split("T")[0]);
  };

  const renderDays = () => {
    const days = [];
    const totalDays = daysInMonth(currentMonth.getMonth(), currentYear);

    for (let i = 1; i <= totalDays; i++) {
      days.push(
        <div
          key={i}
          onClick={() => handleDateClick(i)}
          className={`cursor-pointer p-2 text-center border rounded-lg text-sm ${
            selectedDate?.getDate() === i && selectedDate?.getMonth() === currentMonth.getMonth() &&
            selectedDate?.getFullYear() === currentYear
              ? "bg-yellow-500 text-black"
              : "bg-gray-100 text-black"
          }`}
        >
          {i}
        </div>
      );
    }

    return days;
  };

  const handleMonthChange = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const handleYearChange = (direction) => {
    setCurrentYear((prevYear) => prevYear + direction);
  };

  const handleDecadeChange = (direction) => {
    setCurrentYear((prevYear) => prevYear + direction * 10);
  };

  return (
    <div className="custom-calendar sm:w-[80%]  p-3 border rounded-lg">
      <div className="flex justify-between items-center mb-2 gap-2">
        <button
          onClick={() => handleMonthChange(-1)}
          className="px-2 py-1 bg-gray-200 rounded-lg text-sm text-black"
        >
          Prev
        </button>
        <span className="font-semibold text-sm">
          {currentMonth.toLocaleString("default", { month: "long" })} {currentYear}
        </span>
        <button
          onClick={() => handleMonthChange(1)}
          className="px-2 py-1 bg-gray-200 rounded-lg text-sm text-black"
        >
          Next
        </button>
      </div>
      <div className="flex justify-between items-center mb-2 gap-2">
        <button
          onClick={() => handleDecadeChange(-1)}
          className="px-2 py-1 bg-gray-200  text-black rounded-lg text-sm"
        >
          -10 Years
        </button>
        <button
          onClick={() => handleYearChange(-1)}
          className="px-2 py-1 bg-gray-200 rounded-lg text-sm text-black"
        >
          -1 Year
        </button>
        <span className="font-semibold text-sm">{currentYear}</span>
        <button
          onClick={() => handleYearChange(1)}
          className="px-2 py-1 bg-gray-200 rounded-lg text-sm text-black"
        >
          +1 Year
        </button>
        <button
          onClick={() => handleDecadeChange(1)}
          className="px-2 py-1 bg-gray-200 rounded-lg text-sm text-black"
        >
          +10 Years
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {renderDays()}
      </div>
    </div>
  );
};

const Questions = () => {
  const [quizData, setQuizData] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showTablePage, setShowTablePage] = useState(localStorage.getItem("@userPhone"));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchQuestions = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${base_url}/no-host-questions/list`
        );
        const data = await response.json();
        if (data.status && Array.isArray(data.data)) {
          const formattedQuestions = data.data.map((item) => ({
            id: item.id,
            question: item.question,
            options: item.ques_options || [],
          }));
          setQuizData(formattedQuestions);
        } else {
          throw new Error("Invalid response structure");
        }
      } catch (err) {
        console.error("Error fetching questions:", err);
        setError("Failed to load questions. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, []);

  const handleAnswer = (id) => {
    setAnswers((prev) => ({ ...prev, [currentQuestion]: id }));
    localStorage.setItem("email", id);
    if (currentQuestion < quizData.length - 1) setCurrentQuestion((c) => c + 1);
  };

  const handlePrev = () => {
    // If we're already at the first question, reload the page as requested.
    if (currentQuestion === 0) {
      window.location.reload();
      return;
    }
    setCurrentQuestion((c) => Math.max(0, c - 1));
  };

  const handleSubmit = async () => {
    try {
      const submission = Object.keys(answers).map((key) => ({
        question_id: quizData[key]?.id,
        answer: answers[key],
      }));

      const payload = {
        user_id: localStorage.getItem("@userAccount"),
        answers: submission,
      };

      await submitAnswers(payload);
      setShowTablePage(true);
    } catch (error) {
      console.error("Submission error:", error);
    }
  };

  const progress = quizData.length ? ((currentQuestion + 1) / quizData.length) * 100 : 0;

  if (loading) {
    return <div className="text-center mt-10">Loading questions...</div>;
  }

  if (error) {
    return <div className="text-center mt-10 text-red-600">{error}</div>;
  }

  const renderSpecialInput = (question) => {
    if (question.toLowerCase().includes("dob")) {
      return <CustomDatePicker onChange={(value) => handleAnswer(value)} />;
    }

    if (question.toLowerCase().includes("phone")) {
        ReactPixel.track("OTP", {
             model: 'NO_HOST'
       });
        ReactPixel.track("Verified", {
             model: 'NO_HOST'
       });
      return <PhoneInput onOtpVerified={() => handleAnswer("phone-verified")} />;
    }

    if (question.toLowerCase().includes("email")) {
      return <EmailInput onComplete={(data) => { handleAnswer(data.email); handleSubmit(); }} />;
    }

    return null;
  };

  return (
    <div className="ht-questions-root w-full mx-auto md:px-20">
   

      <div className="w-full md:w-4xl   mx-auto pt-3 md:pt-6 pb-20">
        {showTablePage ? (
          <ShowTable onBack={() => setShowTablePage(false)} />
        ) : (
          <>
          
             <div className="mt-3  mb-10 flex justify-between">
        <button
          onClick={handlePrev}
          className="px-4 py-2 font-semibold text-lg bg-black text-white rounded-lg flex items-center gap-2"
        >
          <FaArrowLeft /> Back
        </button>
      </div>
            <div className="w-full bg-gray-200 h-2.5 rounded-full mb-6">
              <div
                className="h-2.5 bg-yellow-500 rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>

            {quizData.length === 0 ? (
              <div>
                <h2 className="text-2xl md:text-3xl text-gray-800 font-semibold mt-2 mb-4">
                  Sorry no questions available
                </h2>
              </div>
            ) : (
              <div className="md:w-4xl">
                <div className="ht-hero-and-choices">
                  <h1 className="text-left text-2xl md:text-4xl font-semibold w-full mb-10 text-gray-800">
                    {quizData[currentQuestion]?.question}
                  </h1>

                  {quizData[currentQuestion]?.options.length > 0 ? (
                    <div className="grid sm:grid-cols-2 -mt-1 sm:-mt-0 gap-4 w-full">
                      {quizData[currentQuestion]?.options.map((option, idx) => {
                        const letter = String.fromCharCode(65 + idx);
                        const isFull =
                          quizData[currentQuestion]?.options.length === 3 && idx === 2;
                        return (
                          <div
                            key={idx}
                            onClick={() => handleAnswer(option)}
                            className={`ht-choice-card sm:hover:bg-[#f7dba0] ${isFull ? "ht-choice-full" : ""} ${
                              answers[currentQuestion] === option ? "selected" : ""
                            }`}
                          >
                            <div className="ht-badge">{letter}</div>
                            <div className="text-sm sm:text-lg text-gray-800">{option}</div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div>{renderSpecialInput(quizData[currentQuestion]?.question)}</div>
                  )}
                </div>

                {/* {currentQuestion === quizData.length - 1 && (
                  <div
                    onClick={handleSubmit}
                    className="w-full p-3 md:w-72 mt-8 bg-black border rounded-2xl text-sm md:text-base text-gray-100 cursor-pointer text-center"
                  >
                    Submit Response
                  </div>
                )} */}
              </div>
            )}
          </>
        )}
      </div>

      <SuccessModal setIsModalOpen={setIsModalOpen} isModalOpen={isModalOpen} />
    </div>
  );
};

export default Questions;

